import React, {
  FC,
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';

import {
  Box,
  Flex,
  Text,
  Input,
  Select,
  chakra,
  Modal,
  Button,
  Divider,
  Spinner,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
  useToast,
  Center,
  CircularProgress,
} from '@chakra-ui/react';
import { LuSplit } from 'react-icons/lu';

import { useSKU } from 'hooks';

import { SkuItem, TransferFormModalViewProps } from './TransferFormModal.props';
import { useQuery, useQueryClient } from 'react-query';
import { getCaseAvailability, getItemAvailability } from 'api/items';
import { getPackageAvailability } from 'api/packages';
import { getEquipmentAvailability } from 'api/equipment';
import { isEquipmentTransfersEnabled } from 'constants/featureFlags';
import { selectors } from 'store/slices/washhub';
import { MdCheck, MdClose, MdRemoveCircle } from 'react-icons/md';
import { Status } from 'types/items';

const headingMap = {
  0: 'Transfer Out',
  1: 'Transfer In',
} as const;

const labelProps = {
  fontSize: '14px',
  color: '#afafaf',
  fontWeight: '550',
};

const TransferHubWashhub = `6552e877b476bf1a0f578a06`;

const Label = chakra('label');
const Section = chakra('section');

type ValuesRef = {
  items: SkuItem[];
  cases: SkuItem[];
  packages: SkuItem[];
  equipment: SkuItem[];
};

const TransferFormModalView: FC<TransferFormModalViewProps> = ({
  type,
  isOpen,
  onClose,
  onCreate,
  isLoading,
  isSuccess,
  addDelayOnClose,
}) => {
  const toast = useToast();

  const valuesRef = useRef<ValuesRef>({
    items: [],
    cases: [],
    packages: [],
    equipment: [],
  });

  const [isValid, setIsValid] = useState(true);
  const [isConfirm, setIsConfirm] = useState(false);

  const washHub = selectors.useSelectedWashHubId();

  const itemsRef = useRef<any>(null);
  const casesRef = useRef<any>(null);
  const packagesRef = useRef<any>(null);
  const equipmentRef = useRef<any>(null);

  // Equipment in Transfers is behind a flag until the client approves it.
  const equipmentEnabled = isEquipmentTransfersEnabled();

  const handleSubmit = () => {
    const items = itemsRef.current?.getItems() || [];
    const cases = casesRef.current?.getCases() || [];
    const packages = packagesRef.current?.getPackages() || [];
    const equipment: SkuItem[] = equipmentEnabled
      ? equipmentRef.current?.getEquipment() || []
      : [];

    const isEmpty =
      items.length === 0 &&
      cases.length === 0 &&
      packages.length === 0 &&
      equipment.length === 0;

    if (isEmpty) {
      toast({
        status: 'warning',
        isClosable: true,
        title: equipmentEnabled
          ? 'Please Select at least 1 of the item/case/package/equipment'
          : 'Please Select at least 1 of the item/case/package',
      });

      return;
    }

    if (!isConfirm) {
      setIsConfirm(true);

      return;
    }

    valuesRef.current = {
      items,
      cases,
      packages,
      equipment,
    };

    onCreate({
      items,
      cases,
      washHub,
      packages,
      equipment,
      operation: type === 0 ? Status.TRANSFER_OUT : Status.TRANSFER_IN,
    });
  };

  return (
    <Modal
      scrollBehavior="inside"
      isCentered
      isOpen={isOpen}
      onClose={() => {
        setIsConfirm(false);
        onClose();
      }}>
      <ModalOverlay />
      <ModalContent borderRadius="10px" w="75vw" maxW="1400px" p={0}>
        <ModalCloseButton _focus={{ outline: 'none' }} />
        {isLoading ? (
          <ModalBody>
            <Center gap="20px" flex={1} minH="500px" flexDirection="column">
              <CircularProgress
                isIndeterminate
                size="100px"
                color="primary.500"
              />
              <Text fontSize="4xl">Transfer being processed</Text>
            </Center>
          </ModalBody>
        ) : isSuccess ? (
          <Summary
            values={valuesRef.current}
            onClose={() => {
              onClose();
              addDelayOnClose();
            }}
            isTransferIn={type === 1}
          />
        ) : (
          <>
            <ModalHeader color="support.500" textAlign="center">
              {isConfirm ? 'Confirm transfer' : headingMap[type]}
              <Text
                textAlign="center"
                fontSize="18px"
                fontWeight="500"
                color="#A3A3A3">
                {isConfirm
                  ? 'Please double check everything'
                  : equipmentEnabled
                  ? 'Select the item, cases, packages and/or equipment you wish to transfer'
                  : 'Select the item, cases and/or packages you wish to transfer'}
              </Text>
            </ModalHeader>
            <ModalBody p={0}>
              <Packages
                ref={packagesRef}
                setIsValid={setIsValid}
                isConfirm={isConfirm}
                type={type}
              />

              <Items
                ref={itemsRef}
                type={type}
                setIsValid={setIsValid}
                isConfirm={isConfirm}
              />

              <Cases
                ref={casesRef}
                type={type}
                setIsValid={setIsValid}
                isConfirm={isConfirm}
              />

              {equipmentEnabled && (
                <Equipment
                  ref={equipmentRef}
                  type={type}
                  setIsValid={setIsValid}
                  isConfirm={isConfirm}
                />
              )}
            </ModalBody>
            <ModalFooter justifyContent="center" gap="10px">
              <Button
                variant="outline"
                w="180px"
                onClick={() => {
                  if (isConfirm) {
                    setIsConfirm(false);
                  } else {
                    onClose();
                  }
                }}
                isDisabled={isLoading}>
                {isConfirm ? 'Go Back' : 'Cancel'}
              </Button>
              <Button
                w="180px"
                onClick={handleSubmit}
                isLoading={isLoading}
                isDisabled={!isValid}>
                {isConfirm ? 'Create' : 'Continue'}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default TransferFormModalView;

const Summary: FC<{
  values: ValuesRef;
  onClose: () => void;
  isTransferIn: boolean;
}> = ({ values, onClose, isTransferIn }) => {
  const queryClient = useQueryClient();

  const washHub = selectors.useSelectedWashHubId();

  const { data: itemSkus } = useSKU(
    'item',
    isTransferIn ? TransferHubWashhub : washHub,
  );
  const { data: caseSkus } = useSKU(
    'case',
    isTransferIn ? TransferHubWashhub : washHub,
  );
  const { data: packSkus } = useSKU(
    'package',
    isTransferIn ? TransferHubWashhub : washHub,
  );
  // Equipment is not assigned to hubs, so the hub filter is skipped.
  const { data: equipmentSkus } = useSKU(
    'equipment',
    undefined,
    undefined,
    undefined,
    true,
  );

  return (
    <ModalBody>
      <Flex flex={1} justify="center" gap="20px">
        <Box transform="rotate(90deg)" display="inline">
          <LuSplit size="35px" strokeWidth={2.5} color="#E6973A" />
        </Box>
        <Text
          fontWeight="600"
          fontSize="2xl"
          color="support.500"
          textAlign="center">
          Transfer initiated
        </Text>
      </Flex>
      <Text textAlign="center" fontSize="18px" fontWeight="500" color="#D0D0D0">
        Your transfer is being processed. Please check the Transfers screen for
        updates.
      </Text>

      <Flex
        my="20px"
        direction="column"
        flex={1}
        gap="10px"
        borderRadius="10px"
        bg="#EDEDED">
        <Box w="100%" p="20px">
          <Text fontWeight="600" textAlign="center" fontSize="20px">
            Summary
          </Text>
        </Box>
        <Divider m="0" p="-0.5" borderColor="#000" />
        <Flex fontWeight="600" p="5px" flex={1}>
          <Text flex={1}></Text>
          <Text flex={1}>Quantity</Text>
          <Text flex={1}>Total Elements</Text>
        </Flex>
        <Flex gap="20px" p="20px" direction="column">
          <Flex direction="column" gap="10px">
            <Text
              borderBottom="1px solid #ccc"
              fontWeight="600"
              fontSize="16px">
              Items
            </Text>

            {values?.items?.length === 0 ? (
              <Text>No Items selected</Text>
            ) : (
              values?.items.map((item, index) => {
                const sku = itemSkus?.find((i) => i._id === item.sku);

                return (
                  <Flex p="5px" key={item.sku + item.quantity + index} flex={1}>
                    <Text flex={1}>{sku?.name || ''}</Text>
                    <Text flex={1}>{item?.quantity || '0'}</Text>
                    <Text flex={1} />
                  </Flex>
                );
              })
            )}
          </Flex>

          <Flex direction="column" gap="10px">
            <Text
              borderBottom="1px solid #ccc"
              fontWeight="600"
              fontSize="16px">
              Cases
            </Text>

            {values?.cases?.length === 0 ? (
              <Text>No Cases selected</Text>
            ) : (
              values?.cases.map((item, index) => {
                const sku = caseSkus?.find((i) => i._id === item.sku);

                return (
                  <Flex p="5px" key={item.sku + item.quantity + index} flex={1}>
                    <Text flex={1}>{sku?.name || ''}</Text>
                    <Text flex={1}>{item?.quantity || '0'}</Text>
                    <Text flex={1} />
                  </Flex>
                );
              })
            )}
          </Flex>

          <Flex direction="column" gap="10px">
            <Text
              borderBottom="1px solid #ccc"
              fontWeight="600"
              fontSize="16px">
              Packages
            </Text>

            {values?.packages?.length === 0 ? (
              <Text>No Packages selected</Text>
            ) : (
              values?.packages.map((item, index) => {
                const sku = packSkus?.find((i) => i._id === item.sku);

                const skuSize = sku?.items?.reduce(
                  (acc, cur) => acc + cur.quantity,
                  0,
                );
                const totalItems = String(
                  Number(item.quantity || '0') * Number(skuSize || '1'),
                );

                return (
                  <Flex p="5px" key={item.sku + item.quantity + index} flex={1}>
                    <Text flex={1}>{sku?.name || ''}</Text>
                    <Text flex={1}>{item?.quantity || '0'}</Text>
                    <Text flex={1}>{totalItems || '0'}</Text>
                  </Flex>
                );
              })
            )}
          </Flex>

          {isEquipmentTransfersEnabled() && (
            <Flex direction="column" gap="10px">
              <Text
                borderBottom="1px solid #ccc"
                fontWeight="600"
                fontSize="16px">
                Equipment
              </Text>

              {!values?.equipment?.length ? (
                <Text>No Equipment selected</Text>
              ) : (
                values.equipment.map((item, index) => {
                  const sku = equipmentSkus?.find((i) => i._id === item.sku);

                  return (
                    <Flex
                      p="5px"
                      key={item.sku + item.quantity + index}
                      flex={1}>
                      <Text flex={1}>{sku?.name || ''}</Text>
                      <Text flex={1}>{item?.quantity || '0'}</Text>
                      <Text flex={1} />
                    </Flex>
                  );
                })
              )}
            </Flex>
          )}
        </Flex>
      </Flex>

      <Center>
        <Button
          variant="outline"
          onClick={() => {
            onClose();

            queryClient.resetQueries({
              predicate: ({ queryKey }) =>
                queryKey.toString().includes('fetch-transfers'),
            });
          }}>
          Close
        </Button>
      </Center>
    </ModalBody>
  );
};

const Packages = forwardRef<any, any>(
  ({ setIsValid, isConfirm, type }, ref) => {
    const washHub = selectors.useSelectedWashHubId();

    const { data, isLoading } = useSKU('package', washHub);
    const [values, setValues] = useState([{ quantity: '0', sku: '' }]);

    useImperativeHandle(
      ref,
      () => ({
        getPackages: () =>
          values
            .filter((v) => !!v.sku && Number(v.quantity || '0') !== 0)
            .map((v) => ({
              ...v,
              quantity: Number(v.quantity),
            })),
      }),
      [values],
    );

    return (
      <Section mb="20px">
        <Text fontSize="16px" fontWeight="600" ml="10%">
          Packages
        </Text>
        <Divider borderColor="#afafaf" />

        <Flex gap="20px">
          <Flex flex={1} mt="10px" ml="10%" direction="column" mr="20px">
            {values.map((pack, index) => {
              const sku = (data || []).find((s) => s._id === pack.sku);

              const skuSize = sku?.items?.reduce(
                (acc, cur) => acc + cur.quantity,
                0,
              );
              const totalItems = String(
                Number(pack.quantity || '0') * Number(skuSize || '1'),
              );

              return (
                <Flex
                  mt="5px"
                  mb="15px"
                  key={`package-${pack.sku}-${index}`}
                  gap="20px"
                  position="relative"
                  alignItems="flex-end">
                  <Flex direction="column" flex={1} gap="5px">
                    <Label {...labelProps}>SKU Assigned</Label>
                    <Select
                      isDisabled={isLoading || isConfirm}
                      placeholder="Select SKU"
                      defaultValue={pack.sku}
                      onChange={(e) => {
                        const itemIndex = values.findIndex(
                          (_, ind) => ind === index,
                        );

                        const newValues = [...values];

                        newValues[itemIndex] = {
                          ...pack,
                          sku: e.target.value,
                        };

                        setValues(newValues);
                      }}>
                      {data?.map((sku) => (
                        <option key={sku._id} value={sku._id}>
                          {sku.name || ''}
                        </option>
                      ))}
                    </Select>
                  </Flex>

                  <Flex direction="column" flex={1} gap="5px">
                    <Label {...labelProps}>Package Amount</Label>
                    <Input
                      min="0"
                      isReadOnly={isConfirm}
                      type="number"
                      isDisabled={pack.sku === ''}
                      defaultValue={pack.quantity}
                      onChange={(e) => {
                        const itemIndex = values.findIndex(
                          (_, ind) => ind === index,
                        );

                        const newValues = [...values];

                        newValues[itemIndex] = {
                          ...pack,
                          quantity: e.target.value,
                        };

                        setValues(newValues);
                      }}
                    />
                  </Flex>

                  <Flex direction="column" flex={1} gap="5px">
                    <Label {...labelProps}>Total Items</Label>
                    <Input
                      min="0"
                      isReadOnly
                      type="number"
                      value={totalItems}
                    />
                  </Flex>

                  {!isConfirm && (
                    <Box
                      as="button"
                      mb="8px"
                      onClick={() => {
                        const newValues = values.filter(
                          (_, ind) => ind !== index,
                        );

                        setValues(newValues);
                      }}>
                      <MdRemoveCircle color="#DE4444" size="22px" />
                    </Box>
                  )}

                  <CheckAvailability
                    sku={pack.sku}
                    operation={type}
                    setIsValid={setIsValid}
                    quantity={Number(pack.quantity || '0')}
                    type="package"
                  />
                </Flex>
              );
            })}
            {values.length <= 5 && !isConfirm && (
              <Button
                w="100%"
                maxW="300px"
                borderRadius="5px"
                variant="ghost"
                bg="primary.900"
                onClick={() => {
                  setValues((prev) => [
                    ...prev,
                    {
                      sku: '',
                      quantity: '0',
                    },
                  ]);
                }}>
                Add SKU (max 6)
              </Button>
            )}
          </Flex>
        </Flex>
      </Section>
    );
  },
);

const Items = forwardRef<any, any>(({ setIsValid, isConfirm, type }, ref) => {
  const washHub = selectors.useSelectedWashHubId();

  const { data, isLoading } = useSKU('item', washHub);
  const [values, setValues] = useState([{ quantity: '0', sku: '' }]);

  useImperativeHandle(
    ref,
    () => ({
      getItems: () =>
        values
          .filter((v) => !!v.sku && Number(v.quantity || '0') !== 0)
          .map((v) => ({
            ...v,
            quantity: Number(v.quantity),
          })),
    }),
    [values],
  );

  return (
    <Section mb="20px">
      <Text fontSize="16px" fontWeight="600" ml="10%">
        Items
      </Text>
      <Divider borderColor="#afafaf" />

      <Flex gap="20px">
        <Flex flex={1} mt="10px" ml="10%" direction="column" mr="20px">
          {values.map((pack, index) => {
            return (
              <Flex
                mt="5px"
                mb="15px"
                key={`item-${pack.sku}-${index}`}
                gap="20px"
                position="relative"
                alignItems="flex-end">
                <Flex direction="column" flex={1} gap="5px">
                  <Label {...labelProps}>SKU Assigned</Label>
                  <Select
                    isDisabled={isLoading || isConfirm}
                    placeholder="Select SKU"
                    defaultValue={pack.sku}
                    onChange={(e) => {
                      const itemIndex = values.findIndex(
                        (_, ind) => ind === index,
                      );

                      const newValues = [...values];

                      newValues[itemIndex] = {
                        ...pack,
                        sku: e.target.value,
                      };

                      setValues(newValues);
                    }}>
                    {data?.map((sku) => (
                      <option key={sku._id} value={sku._id}>
                        {sku.name || ''}
                      </option>
                    ))}
                  </Select>
                </Flex>

                <Flex direction="column" flex={1} gap="5px">
                  <Label {...labelProps}>Item Amount</Label>
                  <Input
                    min="0"
                    type="number"
                    isReadOnly={isConfirm}
                    isDisabled={pack.sku === ''}
                    defaultValue={pack.quantity}
                    onChange={(e) => {
                      const itemIndex = values.findIndex(
                        (_, ind) => ind === index,
                      );

                      const newValues = [...values];

                      newValues[itemIndex] = {
                        ...pack,
                        quantity: e.target.value,
                      };

                      setValues(newValues);
                    }}
                  />
                </Flex>

                {/* <Flex direction="column" flex={1} gap="5px">
                  <Label {...labelProps}>Total Items</Label>
                  <Input isReadOnly type="number" value={totalItems} />
                </Flex> */}

                {!isConfirm && (
                  <Box
                    as="button"
                    mb="8px"
                    onClick={() => {
                      const newValues = values.filter(
                        (_, ind) => ind !== index,
                      );

                      setValues(newValues);
                    }}>
                    <MdRemoveCircle color="#DE4444" size="22px" />
                  </Box>
                )}

                <CheckAvailability
                  sku={pack.sku}
                  operation={type}
                  setIsValid={setIsValid}
                  quantity={Number(pack.quantity || '0')}
                  type="item"
                />
              </Flex>
            );
          })}
          {values.length <= 5 && !isConfirm && (
            <Button
              w="100%"
              maxW="300px"
              borderRadius="5px"
              variant="ghost"
              bg="primary.900"
              onClick={() => {
                setValues((prev) => [
                  ...prev,
                  {
                    sku: '',
                    quantity: '0',
                  },
                ]);
              }}>
              Add SKU (max 6)
            </Button>
          )}
        </Flex>
      </Flex>
    </Section>
  );
});

const Cases = forwardRef<any, any>(({ setIsValid, isConfirm, type }, ref) => {
  const washHub = selectors.useSelectedWashHubId();

  const { data, isLoading } = useSKU('case', washHub);
  const [values, setValues] = useState([{ quantity: '0', sku: '' }]);

  useImperativeHandle(
    ref,
    () => ({
      getCases: () =>
        values
          .filter((v) => !!v.sku && Number(v.quantity || '0') !== 0)
          .map((v) => ({
            ...v,
            quantity: Number(v.quantity),
          })),
    }),
    [values],
  );

  return (
    <Section mb="20px">
      <Text fontSize="16px" fontWeight="600" ml="10%">
        Cases
      </Text>
      <Divider borderColor="#afafaf" />

      <Flex gap="20px">
        <Flex flex={1} mt="10px" ml="10%" direction="column" mr="20px">
          {values.map((pack, index) => {
            return (
              <Flex
                mt="5px"
                mb="15px"
                key={`case-${pack.sku}-${index}`}
                gap="20px"
                position="relative"
                alignItems="flex-end">
                <Flex direction="column" flex={1} gap="5px">
                  <Label {...labelProps}>SKU Assigned</Label>
                  <Select
                    isDisabled={isLoading || isConfirm}
                    placeholder="Select SKU"
                    defaultValue={pack.sku}
                    onChange={(e) => {
                      const itemIndex = values.findIndex(
                        (_, ind) => ind === index,
                      );

                      const newValues = [...values];

                      newValues[itemIndex] = {
                        ...pack,
                        sku: e.target.value,
                      };

                      setValues(newValues);
                    }}>
                    {data?.map((sku) => (
                      <option key={sku._id} value={sku._id}>
                        {sku.name || ''}
                      </option>
                    ))}
                  </Select>
                </Flex>

                <Flex direction="column" flex={1} gap="5px">
                  <Label {...labelProps}>Case Amount</Label>
                  <Input
                    min="0"
                    type="number"
                    isReadOnly={isConfirm}
                    isDisabled={pack.sku === ''}
                    defaultValue={pack.quantity}
                    onChange={(e) => {
                      const itemIndex = values.findIndex(
                        (_, ind) => ind === index,
                      );

                      const newValues = [...values];

                      newValues[itemIndex] = {
                        ...pack,
                        quantity: e.target.value,
                      };

                      setValues(newValues);
                    }}
                  />
                </Flex>

                {/* <Flex direction="column" flex={1} gap="5px">
                  <Label {...labelProps}>Total Items</Label>
                  <Input isReadOnly type="number" value={totalItems} />
                </Flex> */}

                {!isConfirm && (
                  <Box
                    as="button"
                    mb="8px"
                    onClick={() => {
                      const newValues = values.filter(
                        (_, ind) => ind !== index,
                      );

                      setValues(newValues);
                    }}>
                    <MdRemoveCircle color="#DE4444" size="22px" />
                  </Box>
                )}

                <CheckAvailability
                  sku={pack.sku}
                  operation={type}
                  setIsValid={setIsValid}
                  quantity={Number(pack.quantity || '0')}
                  type="case"
                />
              </Flex>
            );
          })}
          {values.length <= 5 && !isConfirm && (
            <Button
              w="100%"
              maxW="300px"
              borderRadius="5px"
              variant="ghost"
              bg="primary.900"
              onClick={() => {
                setValues((prev) => [
                  ...prev,
                  {
                    sku: '',
                    quantity: '0',
                  },
                ]);
              }}>
              Add SKU (max 6)
            </Button>
          )}
        </Flex>
      </Flex>
    </Section>
  );
});

/**
 * Equipment rows for a transfer (ClickUp 86bbmcvj4). The same shape as
 * Cases, but equipment SKUs are not assigned to hubs, so the hub filter is
 * skipped or the list would be empty.
 */
const Equipment = forwardRef<any, any>(
  ({ setIsValid, isConfirm, type }, ref) => {
    const washHub = selectors.useSelectedWashHubId();

    const { data, isLoading } = useSKU(
      'equipment',
      washHub,
      undefined,
      undefined,
      true,
    );
    const [values, setValues] = useState([{ quantity: '0', sku: '' }]);

    useImperativeHandle(
      ref,
      () => ({
        getEquipment: () =>
          values
            .filter((v) => !!v.sku && Number(v.quantity || '0') !== 0)
            .map((v) => ({
              ...v,
              quantity: Number(v.quantity),
            })),
      }),
      [values],
    );

    return (
      <Section mb="20px">
        <Text fontSize="16px" fontWeight="600" ml="10%">
          Equipment
        </Text>
        <Divider borderColor="#afafaf" />

        <Flex gap="20px">
          <Flex flex={1} mt="10px" ml="10%" direction="column" mr="20px">
            {values.map((pack, index) => {
              return (
                <Flex
                  mt="5px"
                  mb="15px"
                  key={`equipment-${pack.sku}-${index}`}
                  gap="20px"
                  position="relative"
                  alignItems="flex-end">
                  <Flex direction="column" flex={1} gap="5px">
                    <Label {...labelProps}>SKU Assigned</Label>
                    <Select
                      isDisabled={isLoading || isConfirm}
                      placeholder="Select SKU"
                      defaultValue={pack.sku}
                      onChange={(e) => {
                        const itemIndex = values.findIndex(
                          (_, ind) => ind === index,
                        );

                        const newValues = [...values];

                        newValues[itemIndex] = {
                          ...pack,
                          sku: e.target.value,
                        };

                        setValues(newValues);
                      }}>
                      {data?.map((sku) => (
                        <option key={sku._id} value={sku._id}>
                          {sku.name || ''}
                        </option>
                      ))}
                    </Select>
                  </Flex>

                  <Flex direction="column" flex={1} gap="5px">
                    <Label {...labelProps}>Item Amount</Label>
                    <Input
                      min="0"
                      type="number"
                      isReadOnly={isConfirm}
                      isDisabled={pack.sku === ''}
                      defaultValue={pack.quantity}
                      onChange={(e) => {
                        const itemIndex = values.findIndex(
                          (_, ind) => ind === index,
                        );

                        const newValues = [...values];

                        newValues[itemIndex] = {
                          ...pack,
                          quantity: e.target.value,
                        };

                        setValues(newValues);
                      }}
                    />
                  </Flex>

                  {!isConfirm && (
                    <Box
                      as="button"
                      mb="8px"
                      onClick={() => {
                        const newValues = values.filter(
                          (_, ind) => ind !== index,
                        );

                        setValues(newValues);
                      }}>
                      <MdRemoveCircle color="#DE4444" size="22px" />
                    </Box>
                  )}

                  <CheckAvailability
                    sku={pack.sku}
                    operation={type}
                    setIsValid={setIsValid}
                    quantity={Number(pack.quantity || '0')}
                    type="equipment"
                  />
                </Flex>
              );
            })}
            {values.length <= 5 && !isConfirm && (
              <Button
                w="100%"
                maxW="300px"
                borderRadius="5px"
                variant="ghost"
                bg="primary.900"
                onClick={() => {
                  setValues((prev) => [
                    ...prev,
                    {
                      sku: '',
                      quantity: '0',
                    },
                  ]);
                }}>
                Add SKU (max 6)
              </Button>
            )}
          </Flex>
        </Flex>
      </Section>
    );
  },
);

const apiMap = {
  item: getItemAvailability,
  case: getCaseAvailability,
  package: getPackageAvailability,
  equipment: getEquipmentAvailability,
} as const;

/** "All items are available", but "All equipment is available". */
const availabilityText = {
  item: ['All items are available', 'The items aren’t available ⓘ'],
  case: ['All cases are available', 'The cases aren’t available ⓘ'],
  package: ['All packages are available', 'The packages aren’t available ⓘ'],
  equipment: ['All equipment is available', 'The equipment isn’t available ⓘ'],
} as const;

const operationMap = {
  0: Status.TRANSFER_OUT,
  1: Status.TRANSFER_IN,
} as const;

const CheckAvailability: FC<{
  sku: string;
  type: 'package' | 'case' | 'item' | 'equipment';
  quantity: number;
  setIsValid: (value: boolean) => void;
  operation: 0 | 1;
}> = ({ sku, type, quantity, setIsValid, operation }) => {
  const washHub = selectors.useSelectedWashHubId();

  const queryKey = [sku, washHub, operation];
  const queryFn = () => apiMap[type](washHub, sku, operationMap[operation]);
  const options = { enabled: !!sku };

  const { data = 0, isFetching } = useQuery(queryKey, queryFn, options);

  const isValid = data >= quantity;

  useEffect(() => {
    setIsValid(data >= quantity);
  }, [quantity, data]);

  return (
    <Flex w="100%" maxW="250px">
      {!sku ? null : isFetching ? (
        <Flex
          align="center"
          gap="10px"
          bg="#EBEBEB"
          p="10px 15px"
          borderRadius="10px"
          border="1px solid #EBEBEB">
          <Spinner size="sm" />
          <Text fontSize="12px">Checking availability</Text>
        </Flex>
      ) : isValid ? (
        <Flex
          align="center"
          gap="10px"
          bg="#EBEBEB"
          p="10px 15px"
          borderRadius="10px"
          border="1px solid #EBEBEB">
          <Box bg="primary.400" borderRadius="50%" p="1px">
            <MdCheck color="#fff" size="14px" />
          </Box>
          <Text fontSize="12px">{availabilityText[type][0]}</Text>
        </Flex>
      ) : (
        <Flex
          align="center"
          gap="10px"
          bg="#EBEBEB"
          p="10px 15px"
          borderRadius="10px"
          border="1px solid #EBEBEB">
          <Box bg="#DE4444" borderRadius="50%" p="1px">
            <MdClose color="#fff" size="14px" />
          </Box>
          <Text fontSize="12px">{availabilityText[type][1]}</Text>
        </Flex>
      )}
    </Flex>
  );
};
